import loadable from '@loadable/component';
import * as React from 'react';

/**
 * ============================
 * Internal client-only loader
 * ============================
 */

// eslint-disable-next-line @typescript-eslint/consistent-type-imports
type IEthora = typeof import('@ethora/chat-component');
type IEthoraHooks = Pick<IEthora, 'useQRCodeChat'>;
let ethoraModule: IEthora | null = null;

async function loadEthora(): Promise<IEthora> {
  if (typeof window === 'undefined') {
    throw new Error('Ethora accessed during SSR');
  }

  if (!ethoraModule) {
    ethoraModule = await import('@ethora/chat-component');
  }

  return ethoraModule;
}

/**
 * ============================
 * React Components (SSR-safe)
 * ============================
 */

const XmppProvider = loadable(() => import('@ethora/chat-component'), {
  ssr: false,
  resolveComponent: (m) => m.XmppProvider,
});

const Chat = loadable(() => import('@ethora/chat-component'), {
  ssr: false,
  resolveComponent: (m) => m.Chat,
});

/**
 * ============================
 * Unread context + bridge
 * ============================
 *
 * The Ethora useUnread hook can only be called inside XmppProvider and
 * cannot be wrapped with the async useClientHook pattern (doing so
 * changes the number of hook calls between renders). Instead we use a
 * headless bridge component loaded via @loadable that calls the real
 * hook and pushes results into this context.
 */

export interface UnreadState {
  totalCount: number;
  loading: boolean;
  unreadByRoom: Record<string, number>;
}

const defaultUnread: UnreadState = {
  totalCount: 0,
  loading: true,
  unreadByRoom: {},
};

export const UnreadContext = React.createContext<UnreadState>(defaultUnread);
UnreadContext.displayName = 'UnreadContext';

const UnreadBridge = loadable(() => import('./UnreadBridge'), { ssr: false });

function useUnreadMessages(): UnreadState {
  return React.useContext(UnreadContext);
}

/**
 * ============================
 * Functions / services
 * ============================
 */

async function getLogoutService() {
  const m = await loadEthora();
  return m.logoutService;
}

async function handleQRChatId(...args: Parameters<IEthora['handleQRChatId']>) {
  const m = await loadEthora();
  return m.handleQRChatId(...args);
}

async function resendMessage(...args: Parameters<IEthora['resendMessage']>) {
  const m = await loadEthora();
  return m.resendMessage(...args);
}

/**
 * ============================
 * Hooks (client-only wrappers)
 * ============================
 */

function useClientHook<T extends keyof IEthoraHooks>(
  hookName: T
): IEthoraHooks[T] | null {
  const [hook, setHook] = React.useState<IEthoraHooks[T] | null>(null);

  React.useEffect(() => {
    let mounted = true;

    loadEthora().then((m) => {
      if (mounted) {
        setHook(() => m[hookName]);
      }
    });

    return () => {
      mounted = false;
    };
  }, [hookName]);

  return hook;
}

function useQRCodeChat(...args: Parameters<IEthoraHooks['useQRCodeChat']>) {
  if (typeof window === 'undefined') return null;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useClientHook('useQRCodeChat')?.(...args);
}

const Ethora = {
  XmppProvider,
  Chat,
  UnreadBridge,
  UnreadContext,
  getLogoutService,
  handleQRChatId,
  resendMessage,
  useQRCodeChat,
  useUnreadMessages,
};
export default Ethora;
